import {
  BC_NAME_SYNC,
  useApiServices,
  useAppContext,
  useDataContext,
  usePaneContext,
  getVisiblePointCloudIds,
  isPointCloudVisible,
  type ProjectDataState,
} from '@mobile-mapping/providers';
import {
  ContentType,
  Map2DPaneContent,
  PlanarPaneContent,
  PointCloudPaneContent,
  SYNTHETIC_MISSION_TRN_SCAN_DATA_ONLY_POINT_CLOUD,
  syntheticRunIdForScanDataOnlyPointCloud,
  TrimBimPaneContent,
} from '@mobile-mapping/web3d';
import { PaneManager } from '@trimble/pane-layout';
import { createBCSyncer } from '@trimble/utilities';
import { LoadState } from '@trimble/data-loading';
import { createMemo, createResource, JSX, Show } from 'solid-js';
import { StationsLoadingIndicator } from './components/StationsLoadingIndicator';
import { PaneViewer } from './PaneViewer';
import { StationLoadProvider } from './station-load/station-load-context';
import { useConnectProject } from '@trimble/connect-project-providers';

function getOrphanVisiblePointCloudTrns(
  dataState: ProjectDataState,
  missionPointCloudTrns: ReadonlySet<string>
): string[] {
  const visibleIds = getVisiblePointCloudIds(dataState);
  if (visibleIds !== undefined) {
    return visibleIds.filter((id) => !missionPointCloudTrns.has(id));
  }
  if (dataState.pointCloud.state !== LoadState.LoadComplete) {
    return [];
  }
  return Object.keys(dataState.pointCloud.items).filter(
    (id) => isPointCloudVisible(dataState, id) && !missionPointCloudTrns.has(id)
  );
}

export const Web3DContainer = (props: { web3DPath: string }): JSX.Element => {
  const { isImagesLoading } = useAppContext();
  const { selectedProject, selectedRegion, selectedTenantTrn } =
    useConnectProject();
  const dataContext = useDataContext();
  const { mobileMappingApi, scanDataApi } = useApiServices();

  const [isScanDataRegionSupported] = createResource(
    selectedRegion,
    async (region) => {
      return scanDataApi.isScanDataRegionSupported(region);
    }
  );

  const missionTree = createMemo(() => {
    const isRegionSupportCheckResolved =
      isScanDataRegionSupported.state === 'ready' ||
      isScanDataRegionSupported.state === 'errored';
    const isRegionExplicitlyUnsupported =
      isScanDataRegionSupported.state === 'ready' &&
      isScanDataRegionSupported() === false;

    if (!isRegionSupportCheckResolved || isRegionExplicitlyUnsupported) {
      return undefined;
    }
    return mobileMappingApi.listMissionsTreeQuery(selectedTenantTrn);
  });

  const visRuns = createMemo(() => {
    const project = selectedProject();
    if (!missionTree()?.isSuccess || !project) return [];

    const allRunsWithPointCloud = (missionTree()?.data?.missions ?? [])
      .flatMap((m) => m.runs)
      .filter((r) => r.pointCloudTrn != null);

    // While the point-cloud aggregate is still loading, treat visibility as empty so we
    // don't kick off station loading before the user has explicitly loaded any run.
    const visiblePointCloudIds =
      getVisiblePointCloudIds(dataContext.dataState) ?? [];

    const pcState = dataContext.dataState.pointCloud;
    if (
      visiblePointCloudIds.length === 0 &&
      allRunsWithPointCloud.length > 0 &&
      pcState.state === LoadState.LoadComplete &&
      Object.keys(pcState.items).length === 0
    ) {
      // Mission tree has runs but no point clouds registered in project data (SDS-only flows).
      return allRunsWithPointCloud;
    }

    // Only runs whose point cloud is visible in project data are included
    // (respects hide/off states). Dropping this filter would load imaging + SDS
    // for every mission run with a TRN, including clouds the user turned off.
    return allRunsWithPointCloud.filter((r) =>
      visiblePointCloudIds.includes(r.pointCloudTrn!)
    );
  });

  const planarContents = createMemo<PlanarPaneContent[]>((prev) => {
    const project = selectedProject();
    if (!project) {
      return [];
    }
    const tenantTrn = selectedTenantTrn();
    const vRuns = visRuns();
    const missionPointCloudTrns = new Set(
      vRuns
        .map((r) => r.pointCloudTrn)
        .filter((trn): trn is string => Boolean(trn))
    );
    const orphanPointCloudTrns = getOrphanVisiblePointCloudTrns(
      dataContext.dataState,
      missionPointCloudTrns
    );

    const fromMissions = vRuns.map(
      (run): PlanarPaneContent => ({
        type: ContentType.Planar,
        selectedTenantTrn: tenantTrn!,
        selectedProjectId: project.id,
        selectedMissionTrn: run.missionTrn!,
        selectedRunId: run.id!,
        selectedPointCloudTrn: run.pointCloudTrn!,
      })
    );

    const fromScanDataOnlyPointClouds: PlanarPaneContent[] =
      orphanPointCloudTrns.map((pointCloudTrn) => ({
        type: ContentType.Planar,
        selectedTenantTrn: tenantTrn!,
        selectedProjectId: project.id,
        selectedMissionTrn: SYNTHETIC_MISSION_TRN_SCAN_DATA_ONLY_POINT_CLOUD,
        selectedRunId: syntheticRunIdForScanDataOnlyPointCloud(pointCloudTrn),
        selectedPointCloudTrn: pointCloudTrn,
        scanDataOnly: true,
      }));

    const next = [...fromMissions, ...fromScanDataOnlyPointClouds];
    // If nothing has changed, return previous array to prevent unnecessary reference change and reduce effect updates
    if (
      prev &&
      prev.length === next.length &&
      prev.every(
        (p, i) =>
          p.selectedTenantTrn === next[i].selectedTenantTrn &&
          p.selectedProjectId === next[i].selectedProjectId &&
          p.selectedMissionTrn === next[i].selectedMissionTrn &&
          p.selectedRunId === next[i].selectedRunId &&
          p.selectedPointCloudTrn === next[i].selectedPointCloudTrn &&
          p.scanDataOnly === next[i].scanDataOnly
      )
    )
      return prev;
    return next;
  });

  const map2DPaneContents = createMemo<Map2DPaneContent[]>(() => [
    {
      type: ContentType.Map,
    },
  ]);

  const { layouts, setLayouts } = usePaneContext();

  // Empty arrays for content types now handled by DataLoadingService
  const alignmentContents = createMemo<TrimBimPaneContent[]>(() => []);
  const pointCloudContents = createMemo<PointCloudPaneContent[]>(() => []);

  /* eslint-disable solid/reactivity*/
  createBCSyncer(BC_NAME_SYNC.PlanarContents, true, planarContents);
  createBCSyncer(BC_NAME_SYNC.Map2DPaneContent, true, map2DPaneContents);
  /* eslint-enable solid/reactivity*/

  return (
    <div class="relative h-full w-full">
      <Show when={isImagesLoading()}>
        <StationsLoadingIndicator />
      </Show>
      <StationLoadProvider planarContents={planarContents}>
        <PaneManager
          data={{
            alignmentContents,
            pointCloudContents,
            planarContents,
            map2DPaneContents,
          }}
          layouts={layouts}
          setLayouts={setLayouts}
          web3DPath={props.web3DPath}
          allowPopup={false}
          paneElement={PaneViewer}
        />
      </StationLoadProvider>
    </div>
  );
};
